import React, { FC, useCallback, useState } from 'react';

import { Flex, Loader } from '@opendoor/bricks/core';

import { HelloSignInner } from 'sellDirect/components/closingDash/helloSign/HelloSignInner';

import { HelloSignTaskFields_signers } from 'sellDirect/helpers/__generated__/HelloSignTaskFields';
import { SignContractResult, useSignContract } from 'sellDirect/graphql/useSignContract';
import {
  HelloSignGeneralAddendaKeyName,
  HelloSignTaskKeyName,
} from 'sellDirect/helpers/closingTasks';

export type ContractData = {
  signer: HelloSignTaskFields_signers;
  helloSignUrl: string;
};

interface HelloSignStepProps {
  /** Data for opening a hello sign modal (signer and helloSignUrl) */
  contractData: ContractData;
  /** Callback to call after the task is completed, with the result */
  onTaskCompletion: (result: SignContractResult) => void;
  /** Key name for the hello sign task */
  helloSignTaskName: HelloSignTaskKeyName | HelloSignGeneralAddendaKeyName;
  /** The ID for the offer */
  offerId: string;
  /** Optional. The ID for the general addendum from the `signFormAddendums` task */
  addendumId?: string;
}

/**
 * Wizard step that handles displaying a HelloSign Embedded iFrame
 *
 * component-test-check-ignore storybook-check-ignore
 */
export const HelloSignStep: FC<HelloSignStepProps> = ({
  contractData: { signer, helloSignUrl },
  helloSignTaskName,
  offerId,
  addendumId,
  onTaskCompletion,
}) => {
  const [loading, setIsLoading] = useState(false);
  const { signContract } = useSignContract({
    onError: () => onTaskCompletion(SignContractResult.error),
  });

  /** Function to call when a signer signs their contract */
  const onSign = useCallback(async () => {
    setIsLoading(true);
    const signContractResult = await signContract(
      signer.id,
      offerId,
      helloSignTaskName,
      addendumId,
    );

    onTaskCompletion(signContractResult);
  }, [signContract, signer.id, offerId, helloSignTaskName, onTaskCompletion, addendumId]);

  if (loading) return <HelloSignLoader />;

  return <HelloSignInner onSign={onSign} helloSignUrl={helloSignUrl} />;
};

/** Loader to show while we're processing signing a hello sign document */
const HelloSignLoader = () => (
  <Flex height="70%" width="100%" alignItems="center">
    <Loader size={100} shade="blue" />
  </Flex>
);
